import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/profile/account/billing'

/**
 * Profile account billing service: the account's subscription, upcoming
 * invoice, plan changes, invoices, activity, billing details and card.
 */
class ProfileAccountBillingService {
    /**
     * Get the billing summary: subscription and state, scheduled change,
     * entitlements with usage, payment method, credit balance, active discount
     * and the status alert.
     *
     * @returns {Promise<Object>} Billing summary response
     */
    async get() {
        return await apiClient.get(API_INTERNAL_BASE).then((response) => {
            return response.data
        })
    }

    /**
     * Get the live calculation of the next invoice.
     *
     * @returns {Promise<Object>} Upcoming invoice response
     */
    async upcomingInvoice() {
        return await apiClient
            .get(API_INTERNAL_BASE + '/upcoming-invoice')
            .then((response) => {
                return response.data
            })
    }

    /**
     * The plans the owner can choose from: Free, and the paid plans on sale in
     * the currency the account is billed in, each with its prices and what it
     * includes. The price ULIDs are what a change names as its target.
     *
     * @returns {Promise<Object>} `{ data: { free, plans } }`
     */
    async plans() {
        return await apiClient
            .get(API_INTERNAL_BASE + '/plans')
            .then((response) => {
                return response.data
            })
    }

    /**
     * List the account's invoices, newest first, with download and receipt links.
     *
     * @param {Object} [params] - Pagination params
     * @param {number} [params.page] - Page number
     * @param {number} [params.perPage] - Items per page
     * @returns {Promise<Object>} Invoices response (`items` and `pagination`)
     */
    async invoices({ page, perPage } = {}) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/invoices', {
                params: { page, per_page: perPage },
            })
            .then((response) => {
                return response.data
            })
    }

    /**
     * List the billing events that are the owner's to see, newest first.
     *
     * @param {Object} [params] - Pagination params
     * @param {number} [params.page] - Page number
     * @param {number} [params.perPage] - Items per page
     * @returns {Promise<Object>} Activity response (`items` and `pagination`)
     */
    async activity({ page, perPage } = {}) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/activity', {
                params: { page, per_page: perPage },
            })
            .then((response) => {
                return response.data
            })
    }

    /**
     * Work out what a change would do, without making it.
     *
     * @param {Object} change - `{ kind, target_price_ulid?, discount_code?, acknowledged? }`
     * @returns {Promise<Object>} The preview, with the `hash` to confirm it by
     */
    async previewChange(change) {
        return await apiClient
            .post(API_INTERNAL_BASE + '/changes/preview', change)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Make the change a preview described. The API refuses it with a 409 and
     * the new preview (`data.preview`) if the calculation has moved on.
     *
     * @param {Object} change - The same body as the preview, plus `hash`
     * @returns {Promise<Object>} The billing summary as it is afterwards
     */
    async commitChange(change) {
        return await apiClient
            .post(API_INTERNAL_BASE + '/changes', change)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Undo the pending downgrade, interval switch or cancellation.
     *
     * @returns {Promise<Object>} The billing summary as it is afterwards
     */
    async undoScheduledChange() {
        return await apiClient
            .delete(API_INTERNAL_BASE + '/scheduled-change')
            .then((response) => {
                return response.data
            })
    }

    /**
     * What deleting the account would do, for the owner to read before they
     * confirm: the credit that would be forfeited (`proposed.forfeited_credit`,
     * cents by currency) and what else goes with it. Refused with a 409 and
     * `error_code: unpaid_balance` while an invoice is unpaid or in dispute. The
     * delete itself is `ProfileAccountService.remove()`.
     *
     * @returns {Promise<Object>} The engine's preview of `delete_account`
     */
    async deletionPreview() {
        return await apiClient
            .get(API_INTERNAL_BASE + '/account-deletion')
            .then((response) => {
                return response.data
            })
    }

    /**
     * Update the billing details used on invoices: company, address, emails
     * and tax ID. Applies to invoices issued from now on.
     *
     * @param {Object} details - Any of the `billing_*` account fields
     * @returns {Promise<Object>} The account
     */
    async updateDetails(details) {
        return await apiClient
            .patch(API_INTERNAL_BASE + '/details', details)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Open a card setup for Stripe.js.
     *
     * @returns {Promise<Object>} `{ data: { client_secret, stripe_public_key } }`
     */
    async createPaymentMethod() {
        return await apiClient
            .post(API_INTERNAL_BASE + '/payment-method', {})
            .then((response) => {
                return response.data
            })
    }

    /**
     * Save the card Stripe.js has set up as the account's card. If the account
     * is behind on payment, the open invoice is retried with it.
     *
     * @param {string} paymentMethodId - Stripe payment method id
     * @returns {Promise<Object>} The billing summary as it is afterwards, plus
     *   `card_update: { retry_outcome, authentication_url }` saying how the
     *   retry went (`authentication_url` is where the bank step is, if needed)
     */
    async confirmPaymentMethod(paymentMethodId) {
        return await apiClient
            .post(API_INTERNAL_BASE + '/payment-method/confirm', {
                payment_method_id: paymentMethodId,
            })
            .then((response) => {
                return response.data
            })
    }
}

export default new ProfileAccountBillingService()
